
import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useSnackbar } from 'contexts'
import { useCurrentUserCompany } from 'hooks/api'
import { useCompanyMutationsQuery } from 'hooks/api/useCompanyMutationsQuery'
import { CircleCheckBigIcon, CheckCircleIcon, CopyIcon, Trash2, ArrowUpRightIcon, MoreVertical, PencilIcon, InfoIcon, RefreshCw, ChevronDownIcon, MailIcon } from 'lucide-react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import {
  useTheme,
  Typography,
  Stack,
  CircularProgress,
  Box,
  IconButton,
  InputAdornment,
  RadioGroup,
  FormControlLabel,
  Radio,
  Menu,
  MenuItem,
  Divider,
  Collapse,
  Tooltip,
} from '@mui/material'
import { alpha } from '@mui/material/styles'


import { Card, Button, Input, Skeleton, Alert, Modal } from '@repo/ui'

import { getVerifyFeedback } from './verifyFeedback'
import { ScanDestinationPicker, type ScanDestination } from './ScanDestinationPicker'
import { checkSubdomainAvailability } from 'services/subdomainApi'
import { useReferrerState, useCanAccess } from 'hooks'
import { ROUTES } from 'routes'
import { StatusChip, TextField, ConfirmSettingModal, InfoTooltip, PermissionGate } from 'components'
import { SendInstallInstructionsModal } from 'components/feedback/modals'
import { INTEGRATION_STATUSES } from 'constants/statuses'
import { useShopifyConnectionStatus, useDeleteShopifyWidgetPage, useListShopifyWidgetPages } from 'hooks/api/useShopifyIntegration'
import {
  useCurrentInstallation,
  usePrimaryInstallation,
  useDefaultInstallation,
  useWidgetCode,
  useCreateManualInstallation,
  useUpdateInstallation,
  useDeleteInstallation,
  useSetAsPrimary,
  useVerifyInstallation,
} from 'hooks/api/useInstallations'
import shopifyLogo from 'assets/images/integrations/shopify.svg'
import woocommerceLogo from 'assets/images/integrations/woocommerce.svg'
import webflowLogo from 'assets/images/integrations/webflow.svg'
import wixLogo from 'assets/images/integrations/wix.svg'
import squarespaceLogo from 'assets/images/integrations/squarespace.svg'
import bigcommerceLogo from 'assets/images/integrations/bigcommerce.svg'
import { InstallationHandleModal } from 'components/feedback/modals/InstallationHandleModal'
import type { Installation, VerifyInstallationResult } from 'services/installationApi'

interface InstallationFormData {
  installation_url: string
}

/**
 * Brand logo for an integration.
 *
 * The marks are VENDORED (assets/images/integrations/*.svg), not hotlinked. The
 * catalog used to point at vectorlogo.zone, and by the time anyone looked, the
 * WooCommerce and Squarespace URLs had 404'd — so two of seven rows silently
 * degraded to letter tiles. Third-party logo CDNs rot; the repo already vendors
 * card-brand marks (assets/images/visa.svg, mastercard.svg) for exactly this
 * reason, and this follows that precedent.
 *
 * Rendered as a CSS MASK tinted with the brand colour rather than as an <img>.
 * The source files are single-colour glyphs with no fill attribute, so an <img>
 * would paint them black — invisible on a dark card. Masking lets one asset
 * serve both themes and carry the real brand colour.
 *
 * The chip and glyph colours are theme-aware — see the body for why a fixed
 * white chip was rejected.
 */

/** Perceived brightness (ITU-R BT.601). Decides whether a brand colour can carry
 *  itself against the current surface. */
const luminanceOf = (hex: string): number => {
  const h = hex.replace('#', '')
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16)
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255
}

const IntegrationLogo = ({
  name,
  src,
  brandColor,
}: {
  name: string
  src: string
  brandColor: string
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  // A hard white chip was the first attempt. It gave uniform silhouettes, but on a
  // dark UI a row of them reads as bright holes that outweigh the content — and in
  // LIGHT mode a white chip is invisible against the card, so the treatment only
  // existed in one theme. A neutral chip that tracks the theme is uniform in both.
  const tile = isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.primary[50]
  // Brand colour wherever it can carry itself; otherwise the theme's text colour.
  // Squarespace (#000) and BigCommerce (#121118) are near-black BY BRAND and
  // vanish on a dark chip. Computed from luminance rather than a per-brand
  // exception list, so a logo added later can't quietly go invisible.
  const glyph = isDark && luminanceOf(brandColor) < 0.25 ? theme.palette.text.primary : brandColor

  return (
  <Box
    role="img"
    aria-label={`${name} logo`}
    sx={{
      width: 40,
      height: 40,
      borderRadius: '8px',
      flexShrink: 0,
      backgroundColor: tile,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {/* The url() MUST be quoted. Vite inlines these small SVGs as a data: URI
        whose payload contains raw single quotes and parentheses, so unquoted
        `url(data:image/svg+xml,...)` is invalid CSS and the browser drops the
        whole declaration — leaving mask-repeat/position/size applied with no
        mask at all, which renders each tile as a SOLID BLOCK of the brand colour.
        At 22px that passes for a logo in a screenshot; it isn't one. */}
    <div
      aria-hidden
      style={{
        width: 22,
        height: 22,
        backgroundColor: glyph,
        WebkitMaskImage: `url("${src}")`,
        maskImage: `url("${src}")`,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain',
      }}
    />
  </Box>
  )
}

interface SubdomainFormData {
  subdomain: string
}

export const InstallationPage = () => {
  const theme = useTheme()
  const { open: notification } = useSnackbar()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  // §42. Declared with the other hooks — the loading early-return further down
  // would otherwise make these conditionally-called.
  const referrer = useReferrerState()
  // Split so the two acts can be granted separately: choosing where scans go
  // affects every tag already in the wild, and renaming the subdomain changes the
  // company's public address. Backend @Permissions is the real boundary; this is
  // the affordance (§27 — disable in place, don't hide an action you can see).
  const canSetPrimary = useCanAccess({ action: 'installation_set_primary' }).isHasAccess
  const canEditSubdomain = useCanAccess({ action: 'installation_edit_subdomain' }).isHasAccess
  const methods = useForm<InstallationFormData>()
  const {
    handleSubmit,
    formState: { isDirty, isSubmitting },
    reset,
    setValue,
  } = methods

  // Validate the company handle only when the user clicks Save (not as they type,
  // and not re-validating live after a failed submit).
  const subdomainMethods = useForm<SubdomainFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const [handleModalOpen, setHandleModalOpen] = useState(false)
  // VRG-560: "Send install instructions" — emails the snippet below to whoever
  // runs the site. Its own state rather than the shared modal machinery because
  // it carries nothing in (the recipient is typed inside it).
  const [sendInstructionsOpen, setSendInstructionsOpen] = useState(false)
  const [isEditingSubdomain, setIsEditingSubdomain] = useState(false)
  const [verifyResult, setVerifyResult] = useState<VerifyInstallationResult | null>(null)
  // Dismissal is tracked SEPARATELY rather than by nulling verifyResult, because
  // that one value drives two different things: this alert and the "Unverified"
  // status chip beside the card title. Clearing it on dismiss would erase the
  // status along with the explanation — closing a description of a problem is not
  // a claim the problem is gone. Declared with the other hooks; the loading
  // early-return below would otherwise make it conditionally called.
  const [verifyAlertDismissed, setVerifyAlertDismissed] = useState(false)
  // Manual-install collapse override (null = use the connection-based default).
  const [manualExpandedOverride, setManualExpandedOverride] = useState<boolean | null>(null)
  // E-commerce-integrations collapse override (null = use the primary-based default).
  const [ecommerceExpandedOverride, setEcommerceExpandedOverride] = useState<boolean | null>(null)
  // Show only the most-popular few by default; "View more" reveals the rest.
  const [showAllIntegrations, setShowAllIntegrations] = useState(false)
  // Catalog picker, shown only when an integration is already connected. Up here
  // with the other hooks: the loading early-return below would otherwise make this
  // a conditionally-declared hook.
  const [changeIntegrationOpen, setChangeIntegrationOpen] = useState(false)
  // Staged destination awaiting confirmation. Repointing scans affects every tag
  // ALREADY IN THE WILD — the physical objects don't change, so the effect is
  // immediate and retroactive. Gated before applying, so the picker's selection
  // doesn't move until confirmed.
  const [pendingDestination, setPendingDestination] = useState<ScanDestination | null>(null)
  // Subdomain gets BOTH instruments: a Save (it's free text, so partial input is
  // an invalid state and needs a commit point) AND a confirm (renaming changes the
  // company's public address and its DEFAULT install's destination). Staged here
  // between the two.
  const [pendingSubdomain, setPendingSubdomain] = useState<string | null>(null)


  // Real availability probe, run on blur. MUST live up here with the other hooks:
  // this component early-returns a loading skeleton below, and a useState placed
  // after that return runs only on the loaded render — "rendered more hooks than
  // during the previous render". Replaces a "Checking availability…"
  // line that was tied to the save mutation's isSubmitting and checked nothing —
  // the endpoint has existed all along with no caller, so a collision only ever
  // surfaced as a failed save. `idle` while untouched; `checking` in flight.
  const [availability, setAvailability] = useState<
    { state: 'idle' | 'checking' } | { state: 'available' | 'taken' | 'invalid'; subdomain: string }
  >({ state: 'idle' })


  // Fetch hooks
  const { data: companyData, isLoading: isCompanyLoading, refetch: refetchCompany } = useCurrentUserCompany()
  const { data: currentInstallation, isLoading: isInstallationLoading, refetch: refetchInstallation } = useCurrentInstallation()
  const { data: primaryInstallation, isLoading: isPrimaryLoading } = usePrimaryInstallation()
  const { data: defaultInstallation, isLoading: isDefaultLoading, refetch: refetchDefaultInstallation } = useDefaultInstallation()
  const { data: widgetCodeData, isLoading: isWidgetCodeLoading } = useWidgetCode()
  const { data: shopifyConnection } = useShopifyConnectionStatus()
  const { data: widgetPages } = useListShopifyWidgetPages()

  // Mutation hooks
  const createMutation = useCreateManualInstallation()
  const updateMutation = useUpdateInstallation()
  const deleteMutation = useDeleteInstallation()
  const setPrimaryMutation = useSetAsPrimary()
  const verifyMutation = useVerifyInstallation()
  const deleteWidgetPageMutation = useDeleteShopifyWidgetPage()
  // One instance again. VRG-323 added a SECOND one so the scan-defaults card could have
  // its own success toast without firing it on a subdomain rename; that card now lives in
  // Settings → Fields → Defaults and owns its own instance there.
  const { updateSubdomain } = useCompanyMutationsQuery()

  const installedWidgetPage = widgetPages?.pages?.[0]
  const widgetCode = widgetCodeData?.code || ''

  // Set form value when installation loads
  useEffect(() => {
    if (currentInstallation) {
      setValue('installation_url', currentInstallation.installation_url)
    }
  }, [currentInstallation, setValue])

  // Auto-check verification ONCE on load when a manual install URL exists, so the
  // status pill reflects reality without the user clicking. Deliberately a single
  // shot, not a poll loop: each check is a server-side fetch of the customer's
  // site, and "verified" is point-in-time — continuous polling would be wasteful
  // and bot-like for freshness an install aid doesn't need. Re-verify is on demand.
  // Scroll target for the setup section. Declared UP HERE with the other hooks:
  // this component early-returns a loading skeleton further down, and a hook
  // placed after that return runs only on the loaded render.
  // Per-destination scroll targets. "Set it up" used to scroll to the top of the
  // whole setup area, which for "Your own site" left the user looking at the
  // ecommerce card and expected to find their own way down. Each destination now
  // lands on its OWN card.
  const ecommerceCardRef = React.useRef<HTMLDivElement | null>(null)
  const manualCardRef = React.useRef<HTMLDivElement | null>(null)
  // NO auto-verify on mount. This used to fire a verification as soon as an
  // installation existed, which meant opening Settings greeted you with a red
  // "That URL can't be checked" alert about something you had not touched and
  // were not asking about. An error is a response to an action; nobody had taken
  // one. The alert is now raised only by an explicit Verify click.
  //
  // Nothing is lost by dropping it: the destination row already carries a
  // "Not verified" / "Connected" chip, so a silently-broken install still shows
  // its state — just without an alert claiming you did something wrong.

  /*
    SEED WITH `reset`, NOT `setValue` — working-conventions §54, and this form was the
    exact trap that section describes.

    Save is gated on `formState.isDirty`, and `isDirty` is computed against the form's
    `defaultValues`. `useForm()` here is called with NO defaults, so the baseline was `{}`
    forever: `setValue` writes the VALUE without moving the baseline, so RHF was comparing
    a populated form against nothing. `reset(record)` makes the loaded record the baseline,
    which is what "dirty means differs from what is stored" actually requires.

    IT ALSO RUNS UNCONDITIONALLY, where the old effect was wrapped in
    `if (companyData?.subdomain)`. A brand with no subdomain is not a brand to skip — it is
    the case that needs a baseline MOST, because `''` is exactly what a newly typed value
    has to differ from. Legacy companies predate subdomains being reserved at signup
    (`create-signup-company.use-case`), so they carry `''` — the column is NOT NULL, so
    there is no null state, only empty.

    DEPENDS ON THE PRIMITIVE, not on `companyData`/`subdomainMethods`. Both are fresh
    references on many renders, and a `reset` that re-runs mid-edit would wipe what the
    user is typing. Keyed to the stored value, it fires only when that value actually
    changes.

    HONEST SCOPE: this does not have a confirmed link to the "Save not clickable" report on
    dev — I could not reproduce that on this code in any of three variants (subdomain
    present, subdomain empty, and with the blur that fires the availability probe). It is
    fixed because the seeding was wrong by our own documented rule and made the Save gate
    rest on a baseline that never matched reality.
  */
  useEffect(() => {
    subdomainMethods.reset({ subdomain: companyData?.subdomain ?? '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData?.subdomain])

  // Reopen modal after Shopify OAuth redirect
  useEffect(() => {
    const shopifyParam = searchParams.get('shopify')
    if (shopifyParam === 'connected') {
      // Show success notification
      notification({
        type: 'success',
        title: 'Shopify Connected!',
        text: 'Your Shopify store has been connected successfully.',
      })

      // Reopen the installation modal
      setHandleModalOpen(true)

      // Remove the query parameter from URL
      searchParams.delete('shopify')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, notification])

  // Client-side URL helpers (mirror backend rules)
  const normalizeUrl = (value: string): string => {
    const v = (value || '').trim()
    if (!v) return v
    const prefixed = v.startsWith('http://') || v.startsWith('https://') ? v : `https://${v}`
    try {
      const u = new URL(prefixed)
      const path = u.pathname.replace(/\/$/, '')
      return `${u.protocol}//${u.host}${path}${u.search}${u.hash}`
    } catch {
      return v
    }
  }

  const isPublicHttpUrl = (value?: string): boolean => {
    if (!value) return false
    try {
      const prefixed = value.startsWith('http') ? value : `https://${value}`
      const u = new URL(prefixed)
      if (!(u.protocol === 'http:' || u.protocol === 'https:')) return false
      const host = u.hostname.toLowerCase()
      if (host === 'localhost' || host === '::1' || host.endsWith('.local') || host.endsWith('.test') || host.endsWith('.internal')) return false
      const ipv4 = /^\d+\.\d+\.\d+\.\d+$/.test(host)
      if (ipv4) {
        const [a, b] = host.split('.').map(n => parseInt(n, 10))
        if (a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || a === 127 || (a === 169 && b === 254)) return false
      }
      return true
    } catch {
      return false
    }
  }

  const handleCopy = () => {
    if (!widgetCode) {
      notification({
        type: 'error',
        title: 'No Code to Copy',
        text: 'Widget code is not loaded yet. Please wait.',
      })
      return
    }
    navigator.clipboard.writeText(widgetCode)
    notification({
      type: 'success',
      title: 'Code Copied!',
      text: "Paste it into your website's HTML where you want the widget to appear",
    })
  }

  const handleVerify = async () => {
    // FAILURE stays inline, SUCCESS is a toast. They are not the same kind of
    // message and do not want the same instrument:
    //
    //   A failure carries a remedy ("use the address visitors reach from the open
    //   internet, not localhost…") that you read WHILE editing the URL field
    //   directly above it. A toast would take those instructions away after a few
    //   seconds, precisely when they are being used. Errors that require an action
    //   have to persist next to the thing you act on.
    //
    //   A success has nothing to act on, and until now produced NO feedback at all:
    //   the inline panel renders only when `!verified`, the header chip renders
    //   only for checking/unverified, and the sole trace of a successful click was
    //   the button relabelling itself "Verify" -> "Re-verify". The destination row
    //   does flip to "Verified", but it sits ~600px up the page and is off-screen
    //   from the button you just pressed. An invisible outcome is exactly what a
    //   toast is for.
    //
    // The old comment here warned that toast + inline gave "two overlapping
    // notifications for one action". True — but only because both fired on the
    // same outcome. Split by outcome, only ever one appears.
    setVerifyAlertDismissed(false)
    try {
      const result = await verifyMutation.mutateAsync()
      setVerifyResult(result)
      if (result.verified) {
        notification({
          type: 'success',
          title: 'Installation verified',
          text: 'Vera found its code on that page. You can now send scans there.',
        })
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message || error?.message || 'We could not verify the installation. Please try again.'
      setVerifyResult({
        verified: false,
        checked_url: currentInstallation?.installation_url || '',
        status_code: null,
        message,
      })
    }
  }

  const handleSaveInstallation = async (data: InstallationFormData) => {
    try {
      const normalized = normalizeUrl(data.installation_url)
      data.installation_url = normalized

      if (!isPublicHttpUrl(normalized)) {
        methods.setError('installation_url', { type: 'validate', message: 'Enter a public http(s) URL' })
        return
      }

      const mutation = currentInstallation ? updateMutation : createMutation
      await mutation.mutateAsync({ installation_url: data.installation_url })

      notification({
        type: 'success',
        title: 'Installation URL Saved!',
        text: 'Your installation URL has been updated successfully',
      })
      reset(data)
      setVerifyResult(null)
      refetchInstallation()
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to save installation URL'
      methods.setError('installation_url', { type: 'server', message })
      notification({
        type: 'error',
        title: 'Save Failed',
        text: message,
      })
    }
  }

  const handleDeleteInstallation = async () => {
    if (!currentInstallation) return

    try {
      await deleteMutation.mutateAsync(currentInstallation.id)
      reset({ installation_url: '' })
    } catch (error: any) {
      notification({
        type: 'error',
        title: 'Delete Failed',
        text: error?.message || 'Failed to delete installation URL. Please try again.',
      })
    }
  }

  const handlePrimaryChange = async (installationId: string) => {
    try {
      await setPrimaryMutation.mutateAsync(installationId)
      notification({
        type: 'success',
        title: 'Where scans go has been updated',
        text: 'Scans now go to your new destination.',
      })
    } catch (error: any) {
      notification({
        type: 'error',
        title: 'Update Failed',
        text: error?.message || 'Failed to update primary installation',
      })
    }
  }

  const handleInstallClick = () => {
    // REPLACE the picker rather than stacking on top of it. Install is reached from
    // the "Change ecommerce integration" modal, so this used to open a second modal
    // over the first: two scrims doubling the dimming, the catalog still legible
    // behind the thing covering it, and two Cancel buttons on screen that do
    // different things.
    //
    // Nothing is lost by closing it. The catalog exists to answer "which platform?",
    // and by the time you are here that question is answered — and of the seven rows
    // only Shopify is installable, so there is no comparison to return to. Cancelling
    // lands on the card, which is where the outcome shows anyway.
    setChangeIntegrationOpen(false)
    setHandleModalOpen(true)
  }

  const handleModalClose = () => {
    setHandleModalOpen(false)
  }

  const handleDeleteShopifyWidgetPage = async (pageId: string, pageTitle: string) => {
    try {
      await deleteWidgetPageMutation.mutateAsync(pageId)

    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to remove installation'
      notification({
        type: 'error',
        title: 'Removal Failed',
        text: errorMessage,
      })
    }
  }

  if (isCompanyLoading || isInstallationLoading || isWidgetCodeLoading || isPrimaryLoading || isDefaultLoading) {
    // Footprint-parity skeleton (§19): mirrors the stacked install Cards (title +
    // description lines + a content block each) so the page doesn't jump when the
    // real primary-location / install-method cards load in.
    return (
      <Stack spacing={4}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Skeleton variant="rounded" width={180} height={20} />
                <Skeleton lines={2} />
              </Stack>
              <Skeleton variant="rounded" height={i === 0 ? 64 : 120} />
            </Stack>
          </Card>
        ))}
      </Stack>
    )
  }

  /** `.app.vera.inc`, or the dev equivalent. The one place the suffix is decided. */
  const getDomainSuffix = (): string => {
    const hostname = window.location.hostname
    const isDevelopment = hostname.includes('.dev.vera.inc') || hostname.includes('localhost') || import.meta.env.VITE_ENVIRONMENT === 'development'
    return isDevelopment ? '.app.dev.vera.inc' : '.app.vera.inc'
  }

  /**
   * This brand's subdomain, or EMPTY STRING when they have not set one.
   *
   * The no-subdomain branch used to return the literal `'vera.app.vera.inc'`, which was wrong three
   * ways and not merely cosmetic:
   *
   *   - It is not a real host. The pattern is `<subdomain>.app.vera.inc`, so that string reads as
   *     the subdomain "vera" — a name this brand does not own — and does not resolve.
   *   - It hardcoded the PRODUCTION suffix, so on dev it advertised a prod domain.
   *   - It was presented as the brand's OWN installation target: shown in the read-only field, used
   *     as the installation's `url`, and opened by the "View" button. A brand with no subdomain
   *     configured could click View and land on a dead domain that looked like theirs.
   *
   * Empty is the honest answer and was already an anticipated one — the `url` builder below reads
   * `uniqueDomain ? … : null`, so it has always handled this. The field now shows a placeholder and
   * View is disabled rather than navigating to `https://`.
   *
   * The suffix comes from `getDomainSuffix()` rather than being recomputed. Both functions used to
   * derive `isDevelopment` independently, which is two copies of one rule waiting to disagree.
   */
  const getUniqueDomain = (): string =>
    companyData?.subdomain ? `${companyData.subdomain}${getDomainSuffix()}` : ''

  /**
   * The fixed `.app.vera.inc` tail, rendered INSIDE the field as a muted end adornment.
   *
   * Only the leading label is the brand's to choose; the suffix is Vera's and cannot be edited. So
   * it is shown in `text.secondary` against the value's full-contrast `text.primary` — one field
   * whose tail is visibly not yours, rather than a second disabled input pretending to be editable.
   *
   * It is a shared constant because BOTH states need it and this is the second site (§36). The read
   * state showed the whole host as one flat string, so nothing distinguished the editable half; the
   * edit state tried to render this and silently failed — it passed `InputProps={{endAdornment}}`,
   * and the platform `TextField` has no `InputProps` prop, so React dropped it with no warning and
   * the suffix has never appeared while editing.
   *
   * NOT part of the value. The form field, `companyData.subdomain`, the PATCH body and the
   * confirm-modal copy all carry the BARE label; this is chrome beside the input, so widening it to
   * full width cannot start round-tripping the suffix into the column. Verified both directions:
   * the value read on load and the value submitted are unchanged.
   */
  const domainSuffixAdornment = (
    <InputAdornment position="end">
      <Typography
        variant="body2"
        sx={{
          fontSize: 16,
          color: theme.palette.text.secondary,
          whiteSpace: 'nowrap',
        }}
      >
        {getDomainSuffix()}
      </Typography>
    </InputAdornment>
  )

  const handleCheckAvailability = async (raw: string) => {
    const subdomain = (raw || '').toLowerCase().trim()

    // Your OWN current subdomain reads as "taken" from this endpoint — it resolves
    // availability by "does any company own this", and you do. Exempt it, or
    // opening the editor and clicking away would accuse you of colliding with
    // yourself.
    if (!subdomain || subdomain === companyData?.subdomain) {
      setAvailability({ state: 'idle' })
      return
    }

    setAvailability({ state: 'checking' })
    try {
      const result = await checkSubdomainAvailability(subdomain)
      setAvailability({ state: result.available ? 'available' : 'taken', subdomain })
    } catch {
      // A 400 means the format is bad — the field's own pattern rule already says
      // so on submit, and anything else (network) shouldn't masquerade as a
      // verdict. Report neither available nor taken.
      setAvailability({ state: 'invalid', subdomain })
    }
  }

  /*
    Submit stages; the rename itself runs from the confirm.

    EVERY REJECTION PATH NOW SAYS SOMETHING. All three used to be a bare `return`, so a
    Save that rendered fully enabled — `variant="primary"`, gated only on `isDirty` — could
    be clicked and do nothing at all: no modal, no toast, no field error, no console line.
    That is indistinguishable from a broken button, and it is exactly what was reported
    from production ("save is primary, but not clickable"), which I could not reproduce
    locally precisely BECAUSE the failure leaves no trace to reproduce.

    This does not claim to fix that report — the cause is still unknown and may be
    environmental. It makes the cause VISIBLE the next time it happens, which is the
    prerequisite for fixing it and is worth having regardless of whose bug it turns out
    to be. Silent guards on a primary action are their own defect (§46's logic: a control
    the user can press owes them an outcome they can perceive).

    The empty case stays silent on purpose: `required` on the field already renders an
    inline error on submit, and a toast on top would be the same complaint twice.
  */
  const handleSaveSubdomain = async (data: SubdomainFormData) => {
    const next = data.subdomain.toLowerCase().trim()

    if (!companyData?.id) {
      notification({
        type: 'error',
        title: 'Company not loaded',
        text: 'Your company details are still loading. Wait a moment and try again.',
      })
      return
    }
    if (!next) return
    if (next === companyData.subdomain) {
      notification({
        type: 'info',
        title: 'That is already your subdomain',
        text: `${next}${getDomainSuffix()} is the address you have now. Nothing to change.`,
      })
      return
    }
    setPendingSubdomain(next)
  }

  const handleConfirmSubdomain = async () => {
    if (!companyData?.id || !pendingSubdomain) return

    try {
      const subdomain = pendingSubdomain

      await updateSubdomain({
        companyId: companyData.id,
        subdomain,
      })

      notification({
        type: 'success',
        title: 'Company Handle Updated!',
        text: 'Your company handle has been updated successfully. The default installation URL has been updated.',
      })

      setIsEditingSubdomain(false)
      setPendingSubdomain(null)
      refetchCompany()
      refetchDefaultInstallation()
    } catch (error: any) {
      setPendingSubdomain(null)
      const message = error?.response?.data?.message || error?.message || 'Failed to update company handle'
      subdomainMethods.setError('subdomain', { type: 'server', message })
      notification({
        type: 'error',
        title: 'Update Failed',
        text: message,
      })
    }
  }

  const uniqueDomain = getUniqueDomain()
  const isDefaultPrimary = primaryInstallation?.installation_type === 'DEFAULT'
  const isShopifyPrimary = primaryInstallation?.installation_type === 'ECOMMERCE' && primaryInstallation?.platform === 'SHOPIFY'
  const isManualPrimary = primaryInstallation?.installation_type === 'MANUAL'

  // Human label for whichever install is currently primary (the scan-redirect target)
  const primaryLabel =
    primaryInstallation?.installation_type === 'DEFAULT'
      ? 'Vera subdomain'
      : isShopifyPrimary
        ? 'Shopify (e-commerce)'
        : primaryInstallation?.installation_type === 'ECOMMERCE'
          ? 'E-commerce integration'
          : primaryInstallation?.installation_type === 'MANUAL'
            ? 'Manual install'
            : 'Installation'

  // Get installation IDs
  const defaultInstallationId = defaultInstallation?.id
  const shopifyInstallationId = installedWidgetPage?.installation_id || 
    (primaryInstallation?.installation_type === 'ECOMMERCE' && primaryInstallation?.platform === 'SHOPIFY'
      ? primaryInstallation.id
      : undefined)
  const manualInstallationId = currentInstallation?.id

  // Manual install is always shown, but collapses into an expandable once an
  // e-commerce platform is connected (it's the alternative path, not removed).
  // Default: expanded when nothing is connected, collapsed when Shopify is.
  // Deliberately NOT `shopifyConnection?.connected`. That flag is the OAuth link to
  // the store, which SURVIVES uninstalling the Vera widget page — so after an
  // uninstall this card went on showing Shopify as the connected integration while
  // the picker above it correctly read "Not connected". Two derivations of the same
  // word, disagreeing on one screen. On the Installation page "connected" means the
  // widget is actually installed; a linked store with no widget is not an install.
  const isEcommerceConnected = !!shopifyInstallationId
  // The two install cards are mirror-image accordions: each defaults open unless
  // the OTHER one is the active/primary path. Manual collapses once an e-commerce
  // platform is connected; e-commerce collapses once a manual install is primary.
  // (On a fresh company neither is primary, so both start open for discovery.)
  const manualExpanded = manualExpandedOverride ?? !isEcommerceConnected

  // ── Scan destinations (the picker's model) ────────────────────────────────
  // All three are `installation` rows of different types — the subdomain is a
  // DEFAULT row, Shopify an ECOMMERCE row, your own site a MANUAL row — so a
  // single one-of-N control over them matches the data model rather than
  // imposing a metaphor on it.
  //
  // The mock integration catalog (Webflow, WooCommerce, …) is deliberately NOT
  // here: only Shopify is wired (TODO(VERA-405)), and listing six destinations
  // that cannot receive a scan would make the control mostly dead options.
  const manualVerified = verifyResult?.verified === true
  const selectedDestinationKey: ScanDestination['key'] | null = isDefaultPrimary
    ? 'subdomain'
    : isShopifyPrimary
      ? 'shopify'
      : isManualPrimary
        ? 'manual'
        : null

  const scanDestinations: ScanDestination[] = [
    {
      id: defaultInstallationId,
      key: 'subdomain',
      label: 'Your Vera subdomain',
      url: uniqueDomain ? `https://${uniqueDomain}` : null,
      // Always available: it's created at signup and hosted by us, which is the
      // entire point of having it — there is always one valid destination, so
      // the picker is never a dead control on a brand-new company.
      ready: !!defaultInstallationId,
      statusLabel: 'Always available',
      // BRAND, never a warning. `ready` here tracks an INSTALLATION RECORD, which a
      // company can lack while still owning its subdomain — so this row was painting
      // "Always available" in amber, a label and a colour contradicting each other on
      // the one destination that is guaranteed.
      statusTone: 'brand',
    },
    {
      id: shopifyInstallationId,
      key: 'shopify',
      // Parallel with the sibling rows ("Your Vera subdomain" / "Your own site"):
      // all three answer the same question, so they read as one set. The platform
      // isn't hidden — the URL below it is the myshopify.com address.
      label: 'Your ecommerce site',
      url: installedWidgetPage?.url || (isShopifyPrimary ? primaryInstallation?.installation_url ?? null : null),
      ready: !!shopifyInstallationId,
      statusLabel: shopifyInstallationId ? 'Connected' : 'Not connected',
      // Neutral either way. "Not connected" is a true, unalarming fact — plenty of brands
      // will never connect a store — and the row's 0.55 opacity already reads as
      // unavailable. Amber claimed something needs fixing when nothing does.
      statusTone: 'neutral',
    },
    {
      id: manualInstallationId,
      key: 'manual',
      label: 'Your own site',
      url: currentInstallation?.installation_url ?? null,
      // Requires BOTH a saved URL and a passing verification: pointing scans at a
      // page that doesn't carry the snippet sends customers somewhere that can't
      // show them a certificate.
      ready: !!manualInstallationId && manualVerified,
      statusLabel: !manualInstallationId
        ? 'Not set up'
        : manualVerified
          ? 'Verified'
          : 'Not verified',
      // THE ONE ROW THAT CAN GENUINELY WARRANT AMBER, and only in its middle state.
      // "Not set up" is a neutral fact (you have not added your own site). "Verified" is
      // neutral-good. "NOT VERIFIED" is the real problem: a URL was saved and the check
      // is failing, so scans would land on a page that cannot show a certificate. Keeping
      // amber for exactly that case is what makes it mean something when it appears.
      statusTone: manualInstallationId && !manualVerified ? 'warning' : 'neutral',
    },
  ]

  // Jumping to the setup that would make a destination usable. Both the tooltip's
  // "Set it up" button and a click on the unavailable row land here.
  const handleScrollToSetup = (destination: ScanDestination) => {
    if (destination.key === 'shopify') setEcommerceExpandedOverride(true)
    if (destination.key === 'manual') setManualExpandedOverride(true)
    const target =
      destination.key === 'shopify'
        ? ecommerceCardRef.current
        : destination.key === 'manual'
          ? manualCardRef.current
          : ecommerceCardRef.current
    // Expanding is a state update, so the target may not have its final height
    // this frame — defer the scroll to after the Collapse has laid out.
    requestAnimationFrame(() => {
      // 'nearest', not 'start'. 'start' pins the target to the top of the
      // viewport, which from a picker sitting just above it meant a long jump
      // past content the user hadn't asked to leave. 'nearest' moves the minimum
      // needed to bring the card into view.
      target?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }
  const ecommerceExpanded = ecommerceExpandedOverride ?? !isManualPrimary

  // E-commerce integration catalog. Today this is a static list with Shopify
  // wired to the real OAuth flow; Webflow is a MOCK so we can see how a
  // multi-integration list reads. The eventual source is the Integrations
  // table filtered to `category === 'ecommerce'` (TODO(VERA-405) — pull the
  // catalog from there instead of hardcoding).
  const ecommerceIntegrations: {
    id: string
    name: string
    logo: string
    brandColor: string
    description: string
    available: boolean
    // Install count — drives "most popular" ordering. MOCK numbers today; the real
    // value comes from the Integrations table (TODO(VERA-405)).
    installs: number
  }[] = [
    {
      id: 'shopify',
      name: 'Shopify',
      logo: shopifyLogo,
      brandColor: '#7AB55C',
      description: 'Install Vera directly from the Shopify App Store',
      available: true,
      installs: 4200,
    },
    // Everything below is a MOCK to show how a longer catalog reads + scrolls
    // (not yet wired — TODO(VERA-405)).
    {
      id: 'webflow',
      name: 'Webflow',
      logo: webflowLogo,
      brandColor: '#146EF5',
      description: 'Add Vera to your Webflow site',
      available: false,
      installs: 1800,
    },
    {
      id: 'woocommerce',
      name: 'WooCommerce',
      logo: woocommerceLogo,
      brandColor: '#96588A',
      description: 'Add Vera to your WooCommerce store',
      available: false,
      installs: 3100,
    },
    {
      id: 'bigcommerce',
      name: 'BigCommerce',
      logo: bigcommerceLogo,
      brandColor: '#121118',
      description: 'Add Vera to your BigCommerce store',
      available: false,
      installs: 800,
    },
    {
      id: 'wix',
      name: 'Wix',
      logo: wixLogo,
      brandColor: '#0C6EFC',
      description: 'Add Vera to your Wix site',
      available: false,
      installs: 1400,
    },
    {
      id: 'squarespace',
      name: 'Squarespace',
      logo: squarespaceLogo,
      brandColor: '#000000',
      description: 'Add Vera to your Squarespace site',
      available: false,
      installs: 1100,
    },
  ]

  // "Most popular" = highest install count. Sort by installs desc; the default few
  // (first N after sorting) show up front, "View more" animates the rest open.
  const DEFAULT_VISIBLE_INTEGRATIONS = 3
  const sortedIntegrations = [...ecommerceIntegrations].sort((a, b) => b.installs - a.installs)
  const defaultIntegrations = sortedIntegrations.slice(0, DEFAULT_VISIBLE_INTEGRATIONS)
  const extraIntegrations = sortedIntegrations.slice(DEFAULT_VISIBLE_INTEGRATIONS)
  const hasMoreIntegrations = extraIntegrations.length > 0

  // The connected platform, as a catalog entry. Only Shopify can be connected
  // today (the rest are unwired placeholders — TODO(VERA-405)), so this resolves
  // through the Shopify connection rather than a generic lookup; when more
  // platforms land, this becomes a find() over the real integrations table.
  const connectedIntegration = isEcommerceConnected
    ? ecommerceIntegrations.find((i) => i.id === 'shopify') ?? null
    : null

  // One row, reused by the always-visible default list and the animated "extras".
  // `actions: null` renders the row with no trailing buttons, for the card, where
  // the actions now live at card level rather than crammed inside the row.
  const renderIntegrationRow = (
    integration: typeof ecommerceIntegrations[number],
    opts?: { actions?: null | 'uninstall-only' },
  ) => (
    <Stack
      key={integration.id}
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.primary[100]}`,
        p: 2,
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
        flexShrink: 0,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <IntegrationLogo name={integration.name} src={integration.logo} brandColor={integration.brandColor} />
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 500 }}>
              {integration.name}
            </Typography>
            {/* Store authorized but nothing installed — the third state, which used
                to be indistinguishable from "never set up". Uninstalling removes the
                widget page; the OAuth grant on the store SURVIVES it. That changes
                what Install does: InstallationHandleModal skips the store-domain and
                authorize steps and goes straight to naming the page. The modal
                already says so, but only once you're inside it, which is a click too
                late to inform the decision to click. */}
            {integration.id === 'shopify' && shopifyConnection?.connected && !installedWidgetPage && (
              <Typography
                variant="body3"
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  color: theme.palette.text.secondary,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                Store connected
              </Typography>
            )}
          </Stack>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 12 }}>
            {integration.description}
          </Typography>
        </Stack>
      </Stack>
      {opts?.actions === null ? null : integration.id === 'shopify' ? (
        installedWidgetPage ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            {/* No View in the modal: the card behind it already has one, and this
                modal exists to switch or disconnect — a second link out to the same
                page is a third action competing with the two that matter here. */}
            {opts?.actions !== 'uninstall-only' && (
            <Button
              variant="tertiary"
              size="medium"
              startIcon={<ArrowUpRightIcon size={16} strokeWidth={1.5} />}
              onClick={() => installedWidgetPage.url && window.open(installedWidgetPage.url, '_blank', 'noopener,noreferrer')}
              disabled={!installedWidgetPage.url}
            >
              View
            </Button>
            )}
            <Button
              variant="secondary"
              size="medium"
              destructive="hover"
              loading={deleteWidgetPageMutation.isPending}
              onClick={() => handleDeleteShopifyWidgetPage(installedWidgetPage.page_id, installedWidgetPage.title)}
            >
              Uninstall
            </Button>
          </Stack>
        ) : (
          <Button variant="secondary" size="medium" onClick={handleInstallClick}>
            Install
          </Button>
        )
      ) : integration.available ? (
        <Button variant="secondary" size="medium" onClick={handleInstallClick}>
          Install
        </Button>
      ) : (
        <Button variant="secondary" size="medium" disabled>
          Coming soon
        </Button>
      )}
    </Stack>
  )

  const handleSetPrimary = async (
    installationId: string | null | undefined,
    destinationLabel?: string,
  ) => {
    if (!installationId) return

    try {
      await setPrimaryMutation.mutateAsync(installationId)
      // No toast: the ScanDestinationPicker radio visibly moves to the chosen
      // destination once this refetch lands, which is the confirmation.
      refetchInstallation()
    } catch (error: any) {
      notification({
        type: 'error',
        title: 'Update Failed',
        text: error?.message || 'Failed to update primary installation',
      })
    }
  }

  return (
    <Stack spacing={4}>
      {/* WHERE SCANS GO. Selecting a row is "make primary" — see
          ScanDestinationPicker. This replaces a read-only "Primary location"
          summary that restated state shown 200px below (each install card
          carried its own Primary chip), three separate "Make primary" buttons,
          and a paragraph explaining the concept. A one-of-N control doesn't
          need to be described in prose. */}
      <Card>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="body1" sx={{ fontSize: 20 }}>
              Where scans go
            </Typography>
            <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
              {/* "any of your Vera tags", not "your Vera tag" — this destination is
                  company-wide, so the singular read as though it described one particular
                  tag rather than every tag the brand has ever shipped. That distinction is
                  the whole reason changing it is gated behind a confirm. */}
              When someone scans any of your Vera tags, they land here. Your Vera subdomain
              always works; the others need setting up first.
            </Typography>
          </Stack>

          {/* §27: the picker stays visible + disabled with a tooltip naming the missing
              permission, rather than being hidden — this is a container-level gate (the
              whole control), distinct from the picker's own per-row "no tooltip" design
              for not-ready destinations, so it's applied here at the call site instead of
              inside ScanDestinationPicker. Only the permission gate gets the tooltip; a
              transient in-flight mutation doesn't need one. */}
          <Tooltip
            title={canSetPrimary ? '' : "You need the 'Set primary installation' permission. Ask an admin to grant it."}
            arrow
          >
            <Box data-tour="installation-scan-destination">
              <ScanDestinationPicker
                destinations={scanDestinations}
                selectedKey={selectedDestinationKey}
                onSelect={(destination) => setPendingDestination(destination)}
                onSetUp={handleScrollToSetup}
                disabled={setPrimaryMutation.isPending || !canSetPrimary}
              />
            </Box>
          </Tooltip>
        </Stack>
      </Card>

      {/* The company-wide DEFAULT SCAN INSTRUCTIONS card used to sit here (VRG-323).
          Moved to Settings → Fields → Defaults: this page answers "where does a scan
          LAND" (subdomain, destination, embed snippet), and scan instructions answer
          "where on the object do I physically tap" — only the word "scan" connected
          them. Fields is where the company-wide values a product inherits live. */}

      {/* Default Installation - Unique Domain */}
    <Card>
        <Stack spacing={5} data-tour="installation-subdomain">
          {/* Title + Make-primary share the top row; the description sits full-width
              BELOW so it never reflows (and the card never changes height) when the
              button appears/disappears on primary toggle. */}
          <Stack spacing={2} pb={1}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              style={{minHeight: theme.spacing(8)}}
            >
              <Stack direction="row" spacing={3} alignItems="center">
                {/* Titled to match its row in the picker above — the two name
                    the same destination, so they use the same words. */}
                <Typography variant="body1" sx={{ fontSize: 20 }}>
                  Your Vera subdomain
                  {/* Inside the Typography, not a Stack sibling: the Stack's spacing={3}
                      would park the glyph 12px off the word it annotates, and InfoTooltip's
                      default trigger is built to sit inline (ml:1, verticalAlign middle).
                      The card's own sentence describes what the subdomain DOES; the entry
                      gives its form and, via the footer, why renaming it is a public change
                      of address. */}
                  <InfoTooltip term="subdomain" />
                </Typography>
                <StatusChip statuses={INTEGRATION_STATUSES} status="alwaysAvailable" size="small" />
                {isDefaultPrimary && (
                  <StatusChip statuses={INTEGRATION_STATUSES} status="primary" size="small" />
                )}
              </Stack>
            </Stack>
            <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
              Your Vera subdomain is the default destination, created when you signed up. It's always accessible and unique to your company.
            </Typography>
          </Stack>


          <Stack spacing={1}>
            {isEditingSubdomain ? (
              <FormProvider {...subdomainMethods}>
                <form onSubmit={subdomainMethods.handleSubmit(handleSaveSubdomain)}>
                  {/* Save/Cancel sit on the SAME row as the field, in the slot
                      View/Edit occupy in the read state — so switching into edit
                      mode swaps the buttons in place instead of reflowing the card
                      and pushing everything below it down. */}
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Controller
                      name="subdomain"
                      control={subdomainMethods.control}
                      rules={{
                        required: 'Company handle is required',
                        pattern: {
                          value: /^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])?$/,
                          message: 'Must be 3-63 characters, lowercase alphanumeric and hyphens only, cannot start or end with hyphen',
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...subdomainMethods}
                          label=""
                          name="subdomain"
                          placeholder="your_company"
                          value={field.value ?? ''}
                          onValueChange={(value) => {
                            field.onChange(value.toLowerCase())
                            // A verdict for the OLD value would be stale and
                            // misleading the moment a character changes.
                            setAvailability({ state: 'idle' })
                          }}
                          onFieldBlur={() => handleCheckAvailability(field.value ?? '')}
                          // `endAdornment`, which TextField actually declares. This was
                          // `InputProps={{ endAdornment }}` — a prop the component does not have,
                          // so the suffix was dropped silently and never rendered while editing.
                          endAdornment={domainSuffixAdornment}
                        />
                      )}
                    />
                    </Box>
                    <Button
                      type="submit"
                      variant="primary"
                      size="large"
                      loading={subdomainMethods.formState.isSubmitting}
                      disabled={!subdomainMethods.formState.isDirty}
                    >
                      Save
                    </Button>
                    <Button
                      variant="tertiary"
                      size="large"
                      onClick={() => {
                        setIsEditingSubdomain(false)
                        subdomainMethods.reset()
                        setAvailability({ state: 'idle' })
                      }}
                    >
                      Cancel
                    </Button>
                  </Stack>

                  {/* Availability verdict sits BELOW the row, not inside it — in
                      the row it competed with the buttons for width and shifted
                      them as the message changed length. Reports the REAL probe
                      (handleCheckAvailability); the previous version keyed off
                      isSubmitting, so it flashed during the PATCH while checking
                      nothing at all. */}
                  {availability.state === 'checking' && (
                    <Typography variant="body3" color="text.secondary" sx={{ fontSize: 13, mt: 2 }}>
                      Checking availability…
                    </Typography>
                  )}
                  {availability.state === 'available' && (
                    <Typography variant="body3" sx={{ fontSize: 13, mt: 2, color: theme.palette.success.main }}>
                      {availability.subdomain} is available
                    </Typography>
                  )}
                  {availability.state === 'taken' && (
                    <Typography variant="body3" sx={{ fontSize: 13, mt: 2, color: theme.palette.error.main }}>
                      {availability.subdomain} is taken
                    </Typography>
                  )}
                </form>
              </FormProvider>
            ) : (
              <Stack direction="row" spacing={2} alignItems="center">
                {/* READ-ONLY, not disabled. `disabled` was both the wrong
                    semantic and unreadable: the value isn't unavailable, it just
                    isn't editable here (Edit switches to the form), and MUI's
                    disabled styling greys it to the point where you can't read
                    your own subdomain. `readOnly` keeps full text contrast and
                    stays selectable/copyable, which is what you actually want
                    from a URL.

                    The old `onClick` opening the handle modal was dead code:
                    disabled inputs don't fire click events, so it never ran once
                    — leaving two rival edit paths, one of them broken. Editing
                    goes through the Edit button.

                    No sx override needed: @repo/ui Input's own `readOnly` mode
                    already keeps full contrast, sets a default cursor, and
                    suppresses the brand focus border. The commented-out block
                    that used to sit here was re-implementing that by hand. */}
                {/* FULL WIDTH, via the same `flex: 1, minWidth: 0` wrapper the edit state uses.
                    Without it the field is a flex item at its content width and the row's buttons
                    take the rest: measured 196px inside a 936px card, against 203px of text, so
                    `louis-vuitton.app.dev.vera.inc` truncated. A host is long BY CONSTRUCTION —
                    label plus a fixed 13-17 character suffix — so it should span the card rather
                    than be sized to a guess about how short someone's brand name is.

                    `minWidth: 0` is the load-bearing half: a flex item defaults to `min-width:auto`
                    and refuses to shrink below its content, so without it a long value pushes the
                    buttons off instead of the field taking the free space. */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Input
                    fullWidth
                    // The BARE label — the suffix moves to the muted adornment beside it, so the
                    // editable half and the fixed half are told apart at a glance. Display only;
                    // `uniqueDomain` (label + suffix) is still what View opens and what the
                    // installation records as its url.
                    value={companyData?.subdomain ?? ''}
                    // A brand with no subdomain yet gets a prompt, not an empty box — and
                    // emphatically not a fabricated domain (see getUniqueDomain). No suffix
                    // either: a lone `.app.vera.inc` beside a prompt reads as a real address
                    // that is somehow already half-filled.
                    placeholder="No subdomain set yet — use Edit to choose one"
                    endAdornment={companyData?.subdomain ? domainSuffixAdornment : undefined}
                    readOnly
                  />
                </Box>
                {/* size="large" (48px) to sit level with the field beside it
                    (49px) — `medium` is 40px, which read as two short buttons
                    floating next to a taller input. spacing={3} = 12px, the
                    button-group gap (§29). */}
                <Stack direction="row" spacing={3} alignItems="center" justifyContent="center">
                  {/* Both secondary: View and Edit are peer actions on the same
                      record with no primary between them, so mixing tertiary and
                      secondary implied a hierarchy that isn't real. Matches the
                      ecommerce card's View/Change pair. */}
                  <Button
                    variant="tertiary"
                    size="large"
                    // Disabled with no subdomain: there is nothing to view, and the previous
                    // behaviour opened `https://` + a fabricated domain in a new tab.
                    disabled={!uniqueDomain}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!uniqueDomain) return
                      window.open(`https://${uniqueDomain}`, '_blank', 'noopener,noreferrer')
                    }}
                    startIcon={<ArrowUpRightIcon size={16} strokeWidth={1.5} />}
                  >
                    View
                  </Button>
                  {/* §27: stays visible + disabled with a tooltip naming the missing
                      permission (added alongside the ScanDestinationPicker gate above). */}
                  <Tooltip
                    title={canEditSubdomain ? '' : "You need the 'Edit subdomain' permission. Ask an admin to grant it."}
                    arrow
                  >
                    <span style={{ display: 'inline-flex' }}>
                      <Button
                        variant="tertiary"
                        size="large"
                        startIcon={<PencilIcon strokeWidth={1} size={18} />}
                        onClick={() => setIsEditingSubdomain(true)}
                        disabled={!canEditSubdomain}
                        // tertiary: see the comment on the View beside it
                        aria-label="Edit subdomain"
                      >
                        Edit
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Card>

      {/* SET UP — one card per destination, matching the three rows of the picker
          above.
          These are NOT wrapped in their own Stack. They used to be, and that nested
          Stack's spacing stacked with the page Stack's, so the gap between cards 1→2
          was 16px while 2→3 and 3→4 were 28px — the cards weren't siblings, so they
          couldn't share one gap. Every card is now a direct child of the page Stack,
          which makes parity structural rather than something to keep re-tuning. */}
      <>
        <>
          <>
            <>
              {/* E-commerce install */}
              {/* TODO(VERA-405 / VERA-343): show the full set of per-platform Ecommerce
                  app integrations below the manual install (deferred to the Integrations
                  epic VERA-405 — "Future Feature" per VERA-343, see context/tech-debt.md TD-063). */}
              <Box ref={ecommerceCardRef} sx={{ scrollMarginTop: 24 }}>
              <Card>
                <Stack spacing={3}>
                  {/* Title (accordion toggle) + Make-primary share the top row; the
                      description sits full-width below so the card never reflows/changes
                      height when the button toggles. */}
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <Stack
                        direction="row"
                        spacing={3}
                        alignItems="center"
                        style={{minHeight: theme.spacing(8)}}
                        sx={{ cursor: 'pointer', width: 'fit-content' }}
                        onClick={() => setEcommerceExpandedOverride(!ecommerceExpanded)}
                      >
                        <Typography variant="body1" sx={{ fontSize: 20 }}>
                          Your ecommerce site
                        </Typography>
                        {/* "Installed" is implied by Primary and by the row's View/Uninstall
                            actions, so we only surface Primary here. */}
                        {isShopifyPrimary && (
                          <StatusChip statuses={INTEGRATION_STATUSES} status="primary" size="small" />
                        )}
                        <ChevronDownIcon
                          size={18}
                          strokeWidth={1.5}
                          color={theme.palette.text.secondary}
                          style={{ transform: ecommerceExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                        />
                      </Stack>
                    </Stack>
                    <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14 }}>
                      We support popular E-commerce platforms for a quick and easy, integrated, setup.
                    </Typography>
                  </Stack>

                  <Collapse in={ecommerceExpanded} unmountOnExit>
                  <Box pt={1}>
                  {/* CONNECTED: show only the connected platform plus a way to swap it.
                      A company can have exactly one ecommerce install, so listing the
                      other six underneath the live one is noise for a decision already
                      made — and five of those six are "Coming soon" placeholders, so
                      the list mostly advertises things that can't be picked. The
                      catalog moves into the Change modal, where it's the point rather
                      than a distraction.
                      NOT CONNECTED: the catalog stays inline, because then choosing
                      from it IS the task. */}
                  {connectedIntegration ? (
                    // Row and its actions on ONE line, mirroring the subdomain card's
                    // [field] [View] [Edit]. The actions used to sit on a line below,
                    // which read as a separate step rather than as this row's controls.
                    // The row flexes; the buttons keep their width, so both cards end
                    // at the same right edge.
                    // Uninstall is deliberately absent: it's the only destructive
                    // action here and lives one level deeper, in the Change modal
                    // beside the alternatives, so the surface action is "switch" and
                    // disconnecting takes a deliberate step.
                    <Stack direction="row" spacing={3} alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {renderIntegrationRow(connectedIntegration, { actions: null })}
                      </Box>
                      {/* Both TERTIARY, matching the subdomain card. Same weight
                          because they're peer actions with no primary between them;
                          tertiary rather than secondary because a bordered button
                          sitting beside a bordered field/row stacked box inside box
                          and read heavy. Without the border the pair reads as
                          controls ON the row rather than more chrome. */}
                      <Button
                        variant="tertiary"
                        size="large"
                        startIcon={<ArrowUpRightIcon size={16} strokeWidth={1.5} />}
                        onClick={() => installedWidgetPage?.url && window.open(installedWidgetPage.url, '_blank', 'noopener,noreferrer')}
                        disabled={!installedWidgetPage?.url}
                      >
                        View
                      </Button>
                      <Button
                        variant="tertiary"
                        size="large"
                        onClick={() => setChangeIntegrationOpen(true)}
                      >
                        Change
                      </Button>
                    </Stack>
                  ) : (
                  // NOTHING CONNECTED. The catalog used to be listed inline here; it
                  // now lives only in the modal, so the card has exactly two shapes —
                  // the connected integration with its actions, or this. Keeping a
                  // seven-row catalog permanently on the page made "you have nothing
                  // set up" look identical to "here are your options", and six of the
                  // seven rows aren't installable yet anyway.
                  // LEFT-aligned, sitting under the card's description rather than
                  // right-aligned with View/Change in the connected state. Those are
                  // trailing controls ON a row — they're right-aligned because the
                  // integration row pushes them there. Here there's no row, so a
                  // right-aligned button would be alone across a wide empty span from
                  // the text that motivates it. It reads as the next step after the
                  // description, so it starts where the description starts.
                  // No "nothing is connected" sentence: "View integrations" already
                  // implies there are integrations to go look at, and the card's
                  // subtitle covers the rest. Prose whose only job is to introduce
                  // the button beneath it is a line the button already carries.
                  <Stack direction="row">
                    <Button variant="secondary" size="large" onClick={() => setChangeIntegrationOpen(true)}>
                      View all integrations
                    </Button>
                  </Stack>
                  )}
                  </Box>
                  </Collapse>
                </Stack>
              </Card>
              </Box>

              {/* Manual Install — always shown; collapses into an expandable once an
                  e-commerce platform is connected (the alternative path, not removed). */}
              <>
              <Box ref={manualCardRef} sx={{ scrollMarginTop: 24 }}>
              <Card>
                <Stack spacing={4}>
                  {/* Title (accordion toggle) + Make-primary share the top row; the
                      description sits full-width below so the card never reflows/changes
                      height when the button toggles. */}
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <Stack
                        direction="row"
                        spacing={3}
                        alignItems="center"
                        style={{minHeight: theme.spacing(8)}}
                        sx={{ cursor: 'pointer', width: 'fit-content' }}
                        onClick={() => setManualExpandedOverride(!manualExpanded)}
                      >
                        <Typography variant="body1" sx={{ fontSize: 20 }}>
                          Your own site
                        </Typography>
                        {isManualPrimary && (
                          <StatusChip statuses={INTEGRATION_STATUSES} status="primary" size="small" />
                        )}
                        {/* Surface only the states that need attention: in-progress check
                            and the "Not verified" warning. A verified install is implied by
                            Primary + the populated URL, so we don't add a positive badge. */}
                        {currentInstallation && (
                          verifyMutation.isPending ? (
                            <StatusChip statuses={INTEGRATION_STATUSES} status="checking" size="small" />
                          ) : verifyResult && !verifyResult.verified ? (
                            <StatusChip statuses={INTEGRATION_STATUSES} status="unverified" size="small" />
                          ) : null
                        )}
                        <ChevronDownIcon
                          size={18}
                          strokeWidth={1.5}
                          color={theme.palette.text.secondary}
                          style={{ transform: manualExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                        />
                      </Stack>
                    </Stack>
                    <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14 }}>
                      Add Vera to any custom site by pointing Vera to your public domain and installing the Vera code.
                    </Typography>
                  </Stack>

                  <Collapse in={manualExpanded} unmountOnExit>
                  <Stack spacing={3} pt={1}>
                    <Stack pt={3} spacing={2}>
                      <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 500 }}>
                        1. Provide install URL
                      </Typography>
                      <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
                        Enter your company's website URL and the specific page URL where you'll install the code. Vera uses this
                        to redirect people for tag authentication, certificate access, and app notifications.
                      </Typography>

                      <FormProvider {...methods}>
                        <form onSubmit={handleSubmit(handleSaveInstallation)}>
                          {/* alignItems="center" instead of the old `pt: 2.5` on the
                              button — that 10px nudge was hand-compensating for the
                              empty `label=""` slot above the field, so it drifted
                              out of true whenever the field's metrics changed. */}
                          <Stack
                              direction="row"
                              spacing={3}
                              alignItems="center"
                              justifyContent="flex-start">
                            <Controller
                              name="installation_url"
                              control={methods.control}
                              rules={{
                                required: 'Installation URL is required',
                                validate: (v) => (v && isPublicHttpUrl(v)) || 'Enter a public http(s) URL',
                              }}
                              render={({ field, fieldState }) => (
                                <TextField
                                  {...methods}
                                  label=""
                                  name="installation_url"
                                  placeholder="https://www.example.com"
                                  value={field.value ?? ''}
                                  onValueChange={(value) => field.onChange(value)}
                                />
                              )}
                            />
                            <Stack direction="row" spacing={3}>
                              <Button
                                type="submit"
                                variant="primary"
                                size="large"
                                loading={isSubmitting || createMutation.isPending || updateMutation.isPending}
                                disabled={!isDirty}
                              >
                                Save
                              </Button>
                              {/* {currentInstallation && (
                                <Button
                                  variant="secondary"
                                  destructive
                                  size="medium"
                                  onClick={handleDeleteInstallation}
                                  color="error"
                                >
                                  Cg
                                </Button>
                              )} */}
                            </Stack>
                          </Stack>
                        </form>
                      </FormProvider>
                    </Stack>

                    <Stack spacing={2} pt={3}>
                      <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 500 }}>
                        2. Install the Vera code
                        {/* The paragraph below is the HOW (copy the snippet into <head>).
                            The entry is the WHAT: an installation changes where a scan
                            lands, so certificates open under your domain instead of a
                            Vera address. */}
                        <InfoTooltip term="installation" />
                      </Typography>
                      <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
                        Copy the code snippet below and paste it into the {'<head>'} section of your website's HTML. Once the code is added,
                        Vera verifies the setup automatically. You can also click "Verify" to confirm it.
                      </Typography>

                      <Stack spacing={2} sx={{ pt: 1 }}>
                        {/* <Typography variant="body3" color="text.secondary" sx={{ fontSize: 12, fontWeight: 500 }}>
                          Installation code
                        </Typography> */}
                        {/* Code block (full width) with Copy overlaid top-right — it
                            acts on the code (the conventional 'copy code' affordance).
                            Re-verify is a separate action and lives below the block. */}
                        {/* Block and its actions on ONE row, mirroring the subdomain
                            card's [field] [View] [Edit] and the ecommerce card's
                            [row] [View] [Change]. Copy used to float inside the block's
                            top-right corner and Verify sat on its own line below-left,
                            so one card put its two actions in two different places and
                            neither landed on the right edge every other action on this
                            page shares. Top-aligned rather than centred: the block is
                            ~200px tall, and centring would park the buttons in the
                            middle of nothing. */}
                        <Stack direction="row" spacing={3} alignItems="flex-start">
                          <Box
                            component="pre"
                            sx={(t) => ({
                              m: 0,
                              p: 3,
                              flex: 1,
                              minWidth: 0,
                              borderRadius: 2,
                              backgroundColor: t.palette.primary[50],
                              border: `1px solid ${t.palette.primary[100]}`,
                              color: t.palette.text.secondary,
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              lineHeight: 1.6,
                              // Scroll sideways, don't wrap. The snippet is 9 lines, but
                              // two of them are long (minified markup + CSS), so
                              // pre-wrap/break-all reflowed it into ~30 visual lines and
                              // the block read as enormous — the exact impression the
                              // minification was meant to remove. Nobody reads this top
                              // to bottom; there's a Copy button. So show its real shape.
                              whiteSpace: 'pre',
                              overflowX: 'auto',
                            })}
                          >
                            {widgetCode || 'Loading...'}
                          </Box>
                          {/* STACKED, unlike the horizontal pairs on the other two
                              cards — those sit beside a 49px field, where a row is the
                              only thing that fits. This block is ~200px tall, so a
                              column fills the space beside it instead of leaving a gap
                              under two lonely buttons, and it hands ~100px of width back
                              to the code, which scrolls horizontally.
                              Both TERTIARY at size large, matching the other cards:
                              borderless because a bordered button pressed against a
                              bordered block reads as a box inside a box. */}
                          <Stack spacing={3} sx={{ flexShrink: 0, '& > button': { width: '100%' } }}>
                          <Button
                            variant="tertiary"
                            size="large"
                            onClick={handleCopy}
                            disabled={!widgetCode}
                            startIcon={<CopyIcon size={16} strokeWidth={1.5} />}
                            data-tour="installation-widget-snippet"
                          >
                            Copy
                          </Button>
                          <Button
                            variant="tertiary"
                            size="large"
                            onClick={handleVerify}
                            loading={verifyMutation.isPending}
                            disabled={!currentInstallation}
                            startIcon={<RefreshCw size={16} strokeWidth={1.5} />}
                          >
                            {verifyResult?.verified ? 'Re-verify' : 'Verify'}
                          </Button>
                          {/* VRG-560. Third in the stack, after the two actions that
                              operate on the snippet YOURSELF (copy it, check it landed)
                              and before nothing — this is the hand-off, the thing you do
                              when the person who installs it is not you.

                              `variant="tertiary" size="large"` to match its two
                              siblings; the stack's `& > button { width: 100% }` makes
                              all three the same width without setting it here.

                              PermissionGate in its default `disable` mode, so a user
                              without the permission sees the control and a tooltip
                              naming what to ask for, rather than a menu that silently
                              changes shape (§27/§46). It gates on `installation_view`,
                              the same permission that gates this whole section — see the
                              endpoint's own comment for why emailing a snippet you are
                              already allowed to read on this page is not a stronger
                              capability than reading it. */}
                          <PermissionGate permission="installation_view">
                            <Button
                              variant="tertiary"
                              size="large"
                              onClick={() => setSendInstructionsOpen(true)}
                              disabled={!widgetCode}
                              startIcon={<MailIcon size={16} strokeWidth={1.5} />}
                            >
                              Send instructions
                            </Button>
                          </PermissionGate>
                          </Stack>
                        </Stack>

                        {/* Status lives in the pill next to the title; this is the
                            actionable failure guidance. Uses the shared @repo/ui
                            Alert rather than a hand-rolled icon + Typography row
                            (§28), and derives severity/heading/remedy from the
                            server's error_code — see getVerifyFeedback for why one
                            fixed remedy for every failure was wrong. */}
                        {verifyResult && !verifyResult.verified && !verifyAlertDismissed && (() => {
                          const feedback = getVerifyFeedback(verifyResult)
                          return (
                            // Wrapped, and the gap is PADDING on the wrapper rather
                            // than margin on the Alert. This sits in a spaced Stack,
                            // whose `> :not(style) ~ :not(style) { margin-top }` rule
                            // beats an `mt` on the child — an mt:5 here measured 8px,
                            // the Stack's own spacing. Same mechanism that made the
                            // Card margins uneven. Padding on a wrapper is not a
                            // margin, so nothing overrides it: 8 + 12 = 20px, enough
                            // that the alert reads as a response to the Verify button
                            // rather than as part of the snippet above it.
                            <Box sx={{ pt: 3 }}>
                            <Alert
                              severity={feedback.severity}
                              title={feedback.title}
                              description={feedback.description}
                              // Dismissible: it is a response to a click, not a
                              // permanent property of the page, and once you have
                              // read the remedy it is just occupying the space you
                              // need to act on it. The status chip by the title keeps
                              // reporting the failure after this is gone.
                              onClose={() => setVerifyAlertDismissed(true)}
                            />
                            </Box>
                          )
                        })()}
                      </Stack>
                    </Stack>
                  </Stack>
                  </Collapse>
                </Stack>
              </Card>
              </Box>
              </>
            </>
          </>
        </>
      </>

      {/* Catalog picker. Lives in a modal rather than inline because a company can
          only have ONE ecommerce install: once one is connected the list stops
          being a choice and becomes clutter under the live row. In here, browsing
          it is the whole purpose. */}
      <Modal
        title="Change ecommerce integration"
        open={changeIntegrationOpen}
        onClose={() => setChangeIntegrationOpen(false)}
      >
        <Stack spacing={3}>
          <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.5 }}>
            You can connect one ecommerce platform at a time. Installing a different one
            replaces your current connection.
          </Typography>
          {/* The connected platform leads, carrying the Uninstall action. It's the
              only destructive control in this flow and it lives here rather than on
              the card so the surface-level choice stays "switch", not "destroy" —
              you have to open this to disconnect. */}
          {/* Sentence case, not the small-caps eyebrow these started as. The
              all-caps treatment is a fine convention in the abstract, but it was
              the ONLY instance of it in the app — and matching the rest of our own
              UI beats matching a generic pattern. `pt` gives each group room to
              read as a group rather than crowding the text above it. */}
          {connectedIntegration && (
            <Stack spacing={2} sx={{ pt: 2 }}>
              <Typography variant="body3" color="text.secondary" sx={{ fontSize: 13, fontWeight: 600 }}>
                Connected
              </Typography>
              {renderIntegrationRow(connectedIntegration, { actions: 'uninstall-only' })}
            </Stack>
          )}

          <Stack spacing={2} sx={{ pt: 3 }}>
            <Typography variant="body3" color="text.secondary" sx={{ fontSize: 13, fontWeight: 600 }}>
              Available integrations
            </Typography>
            <Stack spacing={2.5}>
              {sortedIntegrations
                .filter((integration) => integration.id !== connectedIntegration?.id)
                .map((integration) => renderIntegrationRow(integration))}
            </Stack>
          </Stack>
          {/* Most of this list is unbuilt (TODO(VERA-405)), so give people the
              place where integrations are actually managed rather than leaving a
              wall of "Coming soon" as the last word. Carries the referrer (§42) so
              Integrations offers a way back here. */}
          <Button
            variant="tertiary"
            size="medium"
            startIcon={<ArrowUpRightIcon size={16} strokeWidth={1.5} />}
            onClick={() => {
              setChangeIntegrationOpen(false)
              navigate(ROUTES.SETTINGS_COMPANY_INTEGRATIONS, { state: referrer })
            }}
            sx={{ alignSelf: 'flex-start' }}
          >
            View integrations
          </Button>
        </Stack>
      </Modal>

      {/* Consequence gate for the scan destination — it re-points every tag already
          in the wild, so it names that rather than saving silently on click. */}
      <ConfirmSettingModal
        open={pendingDestination !== null}
        onClose={() => setPendingDestination(null)}
        onConfirm={() => {
          const d = pendingDestination
          setPendingDestination(null)
          if (d) handleSetPrimary(d.id, d.label)
        }}
        title="Send scans somewhere else?"
        consequence={
          pendingDestination
            ? `Every Vera tag you've already shipped will start resolving to ${pendingDestination.label.charAt(0).toLowerCase()}${pendingDestination.label.slice(1)} the next time it's scanned. Tags in customers' hands are affected immediately.`
            : ''
        }
        confirmLabel="Change destination"
        loading={setPrimaryMutation.isPending}
      />

      <ConfirmSettingModal
        open={pendingSubdomain !== null}
        onClose={() => setPendingSubdomain(null)}
        onConfirm={handleConfirmSubdomain}
        title="Rename your Vera subdomain?"
        consequence={
          pendingSubdomain
            ? `Your public address becomes ${pendingSubdomain}${getDomainSuffix()}. The old address stops working, and any tag currently pointing at your subdomain will resolve to the new one.`
            : ''
        }
        confirmLabel="Rename"
      />

      {/* Installation Handle Modal */}
      <InstallationHandleModal
        open={handleModalOpen}
        onClose={handleModalClose}
      />

      {/* VRG-560 — takes no props beyond open/close: the snippet is rendered
          server-side at send time from the same service that filled the code block
          above, so the modal never carries a copy of it. */}
      <SendInstallInstructionsModal
        open={sendInstructionsOpen}
        onClose={() => setSendInstructionsOpen(false)}
      />
    </Stack>
  )
}
